import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { NumericProps } from '@swyftx/aviary/atoms/Typography/Numeric/Numeric.styles';
import { EnhancedTooltip } from '@swyftx/aviary/molecules/EnhancedTooltip/EnhancedTooltip';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { useMarginAccount } from 'src/lib/margin/hooks/account';

type Props = {
  title?: string;
  lots: string;
  price: string;
  contractSize?: number;
  size?: NumericProps['size'];
  weight?: NumericProps['weight'];
  color?: NumericProps['color'];
  className?: string;
  valueOnly?: boolean;
};

const TotalMarginPositionSize: React.FC<Props> = ({
  title,
  lots,
  price,
  contractSize,
  className,
  valueOnly = false,
  weight = 'bold',
  size = 'medium',
  color = 'primary',
}) => {
  const { currency } = useMarginAccount();
  const { t } = useTranslation('margin');

  const estimatedTotalPositionSize = useMemo(
    () =>
      Big(lots)
        .times(contractSize || 1)
        .times(price),
    [contractSize, lots, price],
  );

  const valueColor = useMemo((): NumericProps['color'] => {
    if (estimatedTotalPositionSize.eq(0)) return 'placeholder';

    return color;
  }, [color, estimatedTotalPositionSize]);

  return (
    <FlexLayout className='w-full' alignItems='start' justifyContent={valueOnly ? 'end' : 'space-between'} spacing={8}>
      {!valueOnly && title && (
        <EnhancedTooltip
          title={t('position.totalPositionSize.tooltip')}
          name={t('position.totalPositionSize.name')}
          side='left'
        >
          <Body color='secondary' underline='dashed' className='flex cursor-pointer gap-4 whitespace-nowrap'>
            {title}
          </Body>
        </EnhancedTooltip>
      )}
      <Numeric weight={weight} size={size} color={valueColor} className={className}>
        ≈{' '}
        {formatCurrency(estimatedTotalPositionSize.round(2, 0), currency, {
          appendCode: true,
          hideCode: false,
        })}
      </Numeric>
    </FlexLayout>
  );
};

export { TotalMarginPositionSize };
